import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sparkles, CheckCircle2, Users, Building2, ArrowRight, Clock, Award, Zap, Briefcase } from 'lucide-react';
import { STATS, WHY_CHOOSE, COMPANY_INFO } from '../data/mock';
import Reveal from '../components/Reveal';
import InstagramFeed from '../components/InstagramFeed';
import LatestArticles from '../components/LatestArticles';
import { useReveal } from '../hooks/useReveal';

const Counter = ({ value }) => {
  const { ref, visible } = useReveal({ threshold: 0.3 });
  const [display, setDisplay] = React.useState('0');
  React.useEffect(() => {
    if (!visible) return;
    const num = parseInt(value.replace(/\D/g, '')) || 0;
    const prefix = value.match(/^\+/) ? '+' : '';
    const suffix = value.match(/[%h]+$/)?.[0] || '';
    const duration = 1600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(prefix + Math.floor(num * eased).toLocaleString('pt-BR') + suffix);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, value]);
  return <span ref={ref}>{display}</span>;
};

const HomePage = () => {
  const DESC = 'HM Talent RH — especialistas em recrutamento, seleção e hunting de talentos. Conectamos as melhores empresas aos profissionais certos com agilidade e alta assertividade.';
  return (
    <div>
      <Helmet>
        <title>HM Talent RH | Recrutamento, Seleção e Hunting de Talentos</title>
        <meta name="description" content={DESC} />
        <meta property="og:title" content="HM Talent RH | Recrutamento, Seleção e Hunting de Talentos" />
        <meta property="og:description" content={DESC} />
      </Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden hm-gradient">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-hm px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="text-white fade-in">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" /> Inovação em Recrutamento
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight">
              Conectamos<br />pessoas e<br /><span className="text-white/90">oportunidades</span>
            </h1>
            <p className="mt-6 text-lg text-white/95 max-w-xl leading-relaxed" style={{ fontFamily: 'Poppins' }}>
              A HM Talent é especialista em recrutamento ágil e assertivo. Encontramos os melhores profissionais para sua empresa e as melhores oportunidades para sua carreira.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/para-empresas" className="btn-hm-white">
                <Building2 className="w-5 h-5" /> Sou Empresa
              </Link>
              <Link to="/para-candidatos" className="btn-hm-ghost">
                <Users className="w-5 h-5" /> Sou Candidato
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-amber-200 aspect-[4/5] max-w-md mx-auto float-soft">
              <img src="https://customer-assets.emergentagent.com/job_talent-match-304/artifacts/pu3agut1_306D3747-F5F8-4192-A942-FDDA7352DAD8.png" alt="Higor Monteiro - CEO HM Talent" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white font-semibold text-sm">{COMPANY_INFO.ceo} – CEO HM Talent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="container-hm px-6 pb-16 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 bg-white/20 backdrop-blur-md border border-white/30 rounded-2xl p-6">
            {STATS.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-extrabold text-white" style={{ fontFamily: 'Montserrat' }}><Counter value={s.value} /></div>
                <div className="text-xs md:text-sm text-white/95 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APRESENTAÇÃO + ESCOLHA */}
      <section className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Quem somos</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900">Recrutamento ágil, humano e assertivo</h2>
            <p className="mt-4 text-lg text-slate-700 leading-relaxed">A HM Talent é uma consultoria especializada em recrutamento e seleção, com mais de 3.000 contratações realizadas e 95% de taxa de assertividade. Atuamos em todo o Brasil, conectando empresas e talentos com agilidade e qualidade.</p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            <Reveal variant="slideRight" delay={0}>
              <Link to="/para-candidatos" className="block group rounded-3xl overflow-hidden hover-lift hm-gradient-warm h-full">
                <div className="p-8 md:p-10 h-full flex flex-col">
                  <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform">
                    <Users className="w-8 h-8 text-hm-orange" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-white/90 font-bold">Para você</p>
                  <h3 className="text-3xl md:text-4xl font-extrabold text-white mt-1">Sou Candidato</h3>
                  <p className="mt-3 text-white/95 leading-relaxed">Cadastre seu currículo, descubra as vagas em aberto, participe do grupo oficial de WhatsApp e acelere sua carreira com nossas dicas exclusivas.</p>
                  <ul className="mt-5 space-y-2 text-sm text-white/95">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Vagas em aberto com filtros avançados</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Cadastro no Banco de Talentos</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Dicas de carreira e currículo</li>
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-2 text-white font-bold text-lg group-hover:gap-3 transition-all">Acessar área do candidato <ArrowRight className="w-5 h-5" /></span>
                </div>
              </Link>
            </Reveal>

            <Reveal variant="slideLeft" delay={100}>
              <Link to="/para-empresas" className="block group rounded-3xl overflow-hidden hover-lift hm-gradient text-white h-full">
                <div className="p-8 md:p-10 h-full flex flex-col relative">
                  <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                  <div className="relative">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                      <Building2 className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-xs uppercase tracking-widest text-white/85 font-bold">Para sua empresa</p>
                    <h3 className="text-3xl md:text-4xl font-extrabold text-white mt-1">Sou Empresa</h3>
                    <p className="mt-3 text-white/95 leading-relaxed">Conheça nossas soluções de Recrutamento e Seleção, Hunting Especializado e Análise Comportamental Solides DISC. Atendimento em todo o Brasil.</p>
                    <ul className="mt-5 space-y-2 text-sm text-white/95">
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Entregas em até 72 horas</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> 95% de assertividade nas contratações</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Atendimento consultivo personalizado</li>
                    </ul>
                    <span className="mt-6 inline-flex items-center gap-2 text-white font-bold text-lg group-hover:gap-3 transition-all">Acessar área da empresa <ArrowRight className="w-5 h-5" /></span>
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="section-padding bg-hm-yellow">
        <div className="container-hm">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Diferenciais</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Por que escolher a HM Talent</h2>
            <p className="mt-3 text-slate-700">Os diferenciais que fazem a HM Talent referência em recrutamento ágil.</p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={i} delay={i * 100} variant="fadeUp" className={`p-7 rounded-2xl hover-lift card-shadow ${i === 1 ? 'hm-gradient text-white' : 'bg-white border border-amber-100'}`}>
                <div className={`text-4xl font-extrabold mb-3 ${i === 1 ? 'text-white' : 'text-hm-orange'}`} style={{ fontFamily: 'Montserrat' }}>{w.num}</div>
                <h3 className={`text-xl font-bold ${i === 1 ? 'text-white' : 'text-slate-900'}`}>{w.title}</h3>
                <p className={`text-sm mt-2 leading-relaxed ${i === 1 ? 'text-white/95' : 'text-slate-700'}`}>{w.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ÚLTIMOS CONTEÚDOS */}
      <LatestArticles />

      {/* CTA FINAL */}
      <section className="section-padding bg-white">
        <div className="container-hm">
          <Reveal className="hm-gradient text-white rounded-3xl p-10 md:p-14 text-center relative overflow-hidden card-shadow">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
            <div className="relative">
              <h2 className="text-3xl md:text-5xl font-extrabold">Comece agora sua jornada com a HM Talent</h2>
              <p className="mt-4 text-white/95 text-lg max-w-2xl mx-auto">Escolha o caminho ideal e dê o próximo passo na sua carreira ou no recrutamento da sua empresa.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/para-empresas" className="btn-hm-white"><Building2 className="w-5 h-5" /> Sou Empresa</Link>
                <Link to="/para-candidatos" className="btn-hm-ghost"><Users className="w-5 h-5" /> Sou Candidato</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <InstagramFeed />
    </div>
  );
};

export default HomePage;
